/**
 * Reimbursement Feature Configuration
 *
 * Centralizes the configurable business rules AND dropdown option sources
 * for the standalone "Reimbursement" feature
 * (src/views/apps/reimbursement/*.vue, src/services/reimbursement-service.js)
 * so finance/admin/dev can tweak required fields, categories, and every
 * dropdown's option list in ONE place without touching component code.
 *
 * Category options come from the same admin-config expense list as Expense
 * Tracker (`GET /api/admin/config/expense/` → `data.category`). Add new
 * category rows in the backend `admin-config` table (see CATEGORY section
 * below) — they appear in Raise/Edit Reimbursement automatically.
 *
 * Backend: this feature talks to its OWN, dedicated `/api/reimbursements`
 * REST routes backed by a new `reimbursements` Postgres table (see
 * `backend-spec/reimbursements/` for the proposed schema/handoff spec) -
 * it does NOT share the `expenses` table/routes used by the older, unrelated
 * "Expense Tracker" feature. Multi-stage approval uses a dedicated `status`
 * column (see `src/utils/reimbursement-permissions.js` and
 * `backend-spec/reimbursements/`). There is NO `claim_type` and NO `due_date`.
 */

import {
  deriveDisplayStatusVariant,
  formatReimbursementStatusLabel,
} from './reimbursement-permissions'

/** @deprecated Category is a live dropdown; kept only for email/list fallbacks. */
export const REIMBURSEMENT_FIXED_CATEGORY = 'Indirect'

// ------------------------------------------------------------------------
// File upload — POST /api/reimbursements currently uses multer().none() on the
// deployed backend (no file parts accepted). UI still collects Supporting
// Documents and appends them as REIMBURSEMENT_FILE_FIELD; uploads will fail
// until backend wires upload.single('expenseFile') (or supporting_document).
// ------------------------------------------------------------------------
export const REIMBURSEMENT_FILE_UPLOAD_ENABLED = true
/** Multipart field name when a supporting document is attached. */
export const REIMBURSEMENT_FILE_FIELD = 'expenseFile'
/**
 * Invoice module uses `supporting_document` for the same purpose. Backend
 * should accept `expenseFile` (preferred for reimbursements); if they align
 * with invoices instead, map that field server-side.
 */
export const REIMBURSEMENT_SUPPORTING_DOC_FIELD = 'supporting_document'
/** Max supporting-document size (must match backend multer limit). */
export const REIMBURSEMENT_MAX_FILE_SIZE_BYTES = 1.5 * 1024 * 1024
export const REIMBURSEMENT_MAX_FILE_SIZE_LABEL = '1.5 MB'

/** @returns {string|null} Error message if file exceeds max size; otherwise null. */
export function getReimbursementFileSizeError(file) {
  if (!file || typeof file.size !== 'number') return null
  if (file.size <= REIMBURSEMENT_MAX_FILE_SIZE_BYTES) return null
  return `Supporting document must be ${REIMBURSEMENT_MAX_FILE_SIZE_LABEL} or smaller.`
}

/**
 * Attachment is mandatory on create. On edit, an existing stored path satisfies
 * the requirement unless the user clears it without uploading a replacement.
 * @returns {string|null} Error message if no attachment; otherwise null.
 */
export function getReimbursementAttachmentRequiredError(file, existingFilePath = null) {
  if (file) return null
  if (existingFilePath) return null
  return 'Supporting document is required. Please attach a receipt, invoice, or proof of payment.'
}

/** Stored attachment key/path from a reimbursement row (list, detail, or edit). */
export function getReimbursementFilePath(row) {
  if (!row || typeof row !== 'object') return null
  const path = row.file_path
    ?? row.filePath
    ?? row.expense_file
    ?? row.expenseFile
    ?? null
  if (path == null || path === '') return null
  const normalized = String(path).trim()
  return normalized || null
}

/** Display name for an attachment (filename segment of S3 key or path). */
export function getReimbursementFileName(filePath) {
  const path = getReimbursementFilePath({ file_path: filePath })
  if (!path) return null
  const segment = path.split('/').filter(Boolean).pop()
  return segment || path
}

// ------------------------------------------------------------------------
// Mandatory fields on the "Raise Reimbursement" form.
// Keys are the local form-field names used in ReimbursementAdd.vue - see
// FIELD_LABELS below for their display labels (used in validation-error
// summaries). Add/remove a key here to change what's required WITHOUT
// touching the form markup - the component reads this array via
// `isFieldRequired()` to decide each field's vee-validate rule.
// ------------------------------------------------------------------------
export const REQUIRED_FIELDS = [
  'itemName',      // Item / Title — mandatory (do not fall back to comment)
  'expenseDate',   // Date
  'category',      // Category (dropdown from admin-config expense)
  'subCategory',   // Sub-category (only enforced when the selected Category actually has sub-categories configured - see isSubCategoryRequired() in ReimbursementAdd.vue)
  'accountName',   // Account / Client
  'vertical',      // Vertical (Service / Infra / Common / Internal)
  'amount',        // Amount
  'paymentMode',   // Payment Mode
  'location',      // Location
  'comment',       // Description / Comment
  'file',          // Supporting Documents — mandatory on create; Edit may keep an existing file_path
  // gstNumber is required only when is_gst = Yes (see Add/Edit forms)
  // business_unit is NOT required and is omitted from create/update payloads.
]

// Human-readable labels for REQUIRED_FIELDS, used in validation-error
// summaries shown to the user on failed submit.
export const FIELD_LABELS = {
  itemName: 'Item / Title',
  expenseDate: 'Date',
  category: 'Category',
  subCategory: 'Sub-category',
  accountName: 'Account / Client',
  vertical: 'Vertical',
  amount: 'Amount',
  paymentMode: 'Payment Mode',
  location: 'Location',
  comment: 'Description / Comment',
  gstNumber: 'GST Number',
  file: 'Supporting Documents',
}

export const ITEM_TITLE_REQUIRED_MESSAGE = 'Item / Title is required.'

/** Non-empty Item / Title, or the standard required message. */
export function getItemTitleRequiredError(itemName) {
  if (itemName != null && String(itemName).trim() !== '') return null
  return ITEM_TITLE_REQUIRED_MESSAGE
}

export function isFieldRequired(fieldKey) {
  return REQUIRED_FIELDS.includes(fieldKey)
}

// ========================================================================
// DROPDOWN OPTION SOURCES
//
// Every dropdown on the Reimbursement form can be driven either by a LIVE
// service call (client-service.js, location-service.js,
// admin-config-service.js) or by a STATIC list defined right here. Each
// field has its own `USE_LIVE_<FIELD>_LIST` boolean toggle plus a static
// options array/map below it:
//
//   - USE_LIVE_..._LIST = true  -> prefer the live service result; if the
//     live call fails or returns an empty list, automatically fall back to
//     the static list below so the form never ends up with an empty
//     dropdown.
//   - USE_LIVE_..._LIST = false -> use ONLY the static list below (the live
//     result, if any, is ignored for that field). Falls back to whatever
//     the live call returned only if the static list itself is empty, so
//     flipping a flag to `false` without filling in the static list first
//     doesn't break the form.
//
// `resolveOptions()` implements this toggle+fallback logic once, generically,
// for every simple (non category-scoped) dropdown. Category/Sub-category
// have their own small helpers because sub-categories are keyed BY category.
//
// All static lists are plain string arrays for easy editing - the component
// maps them to `{ title }` objects to match the shape v-select/vue-select
// expects (same shape the live services already return).
// ========================================================================

export function resolveOptions(useLive, liveOptions, staticOptions) {
  const live = Array.isArray(liveOptions) ? liveOptions : []
  const fallback = Array.isArray(staticOptions) ? staticOptions : []
  if (useLive) {
    return live.length > 0 ? live : fallback
  }
  return fallback.length > 0 ? fallback : live
}

// --- Payment Mode --------------------------------------------------------
// Primary source is the STATIC list below, NOT the live admin-config call.
// Rationale: payment modes are a small, closed, rarely-changing list
// (Cash/Card/UPI/etc.) that doesn't need finance to manage it via the admin
// backend, and keeping it static guarantees the field always works even if
// the shared admin-config endpoint (`AdminConfigService.getConfigExpense()`)
// is slow, down, or changes shape for unrelated reasons. Flip this to `true`
// if the org later wants payment modes to be admin-editable at runtime.
export const USE_LIVE_PAYMENT_MODE_LIST = false
export const PAYMENT_MODE_OPTIONS = ['Cash', 'Card', 'UPI', 'Bank Transfer', 'Cheque']

// --- Account Name / Client -------------------------------------------------
// Primary source is the LIVE client list (`client-service.js` ->
// `GET jobs/client`) by default, to keep today's working behaviour
// (accounts/clients are managed elsewhere and change often). Set to `false`
// and populate ACCOUNT_NAME_OPTIONS to pin the dropdown to a fixed, static
// list of account names instead (e.g. for a stripped-down deployment that
// doesn't need the full client list).
export const USE_LIVE_CLIENT_LIST = true
export const ACCOUNT_NAME_OPTIONS = []

// --- Business Unit ---------------------------------------------------------
// Removed from the Raise/Edit Reimbursement UI. Client does NOT send
// `business_unit` on create/update (field is optional for this flow).
// If the API still returns `'business_unit' is required`, make that field
// optional in the backend Joi/celebrate schema for POST/PUT /reimbursements
// (or stop listing it in required keys). Do not re-expose in the form
// without product sign-off.
export const BUSINESS_UNIT_DEFAULT = ''
/** When false (default), create/update payloads omit `business_unit`. */
export const SEND_BUSINESS_UNIT = false

// --- Vertical --------------------------------------------------------------
// Closed list for reimbursements (extends invoice Service/Infra with
// Common + Internal for shared/internal claims).
export const VERTICAL_OPTIONS = [
  'Service Industry',
  'Infra Industry',
  'Common',
  'Internal',
]

// --- GST Number ------------------------------------------------------------
// Shown when "GST Invoice?" = Yes. User selects a company GSTIN (not a
// free-text GST amount). Prefer live rows from invoice company config
// (`invoiceConfigService.getSattvaCompanyDetails()`); fall back to the
// static list below (same default GSTIN used by Create Invoice).
export const USE_LIVE_GST_NUMBER_LIST = true
export const GST_NUMBER_OPTIONS = ['27ABDCS0586N1ZT']
/**
 * When true (default), create/update send `gst_amount: 0` so legacy NOT NULL
 * columns stay happy. UI no longer collects a GST amount — only `gst_number`.
 */
export const SEND_GST_AMOUNT = true

// --- Category ----------------------------------------------------------
// Primary source is LIVE (`AdminConfigService.getConfigExpense().category`
// via `GET /api/admin/config/expense/`). Same list as Expense Tracker.
//
// WHERE TO ADD NEW CATEGORIES (backend / DB):
//   Table: `admin-config` (same table as Expense Tracker categories)
//   Endpoint that serves them: GET /api/admin/config/expense/
//     → response shape: { status: 'OK', data: { category: [...], sub_category: [...], payment_mode: [...] } }
//   Each category is a row that the expense-config handler groups into `data.category`
//   (typically module_name related to expense, title = display name, e.g. "Travel").
//   Insert a new row the same way existing expense categories were seeded, e.g.:
//     INSERT INTO admin-config (module_name, param_name, title, ...)
//     VALUES ('expense', 'category', 'Your New Category Name', ...);
//   Confirm exact module_name / param_name values against existing category
//   rows in your DB (mirror one working Expense Tracker category row).
//   Optional create API: POST /api/admin/config (AdminConfigService.create).
//   After insert, Raise Reimbursement loads them automatically — no frontend
//   code change needed when USE_LIVE_CATEGORY_LIST is true.
//
// Set USE_LIVE_CATEGORY_LIST = false and fill CATEGORY_OPTIONS only for a
// static offline fallback.
export const USE_LIVE_CATEGORY_LIST = true
export const CATEGORY_OPTIONS = []

// --- Sub-category (keyed by Category) --------------------------------------
// Primary source is LIVE (`AdminConfigService.getConfigExpense().sub_category`,
// where each row is tagged with `param_name: "SUB-CATEGORY - <Category title>"`)
// by default. Set to `false` and populate SUB_CATEGORY_OPTIONS_BY_CATEGORY to
// use a fixed static mapping instead, e.g.:
//   { Travel: ['Cab', 'Flight'], 'Office Supplies': ['Stationery'] }
//
// WHERE TO ADD SUB-CATEGORIES (backend / DB):
//   Same `admin-config` table / GET /api/admin/config/expense/ → data.sub_category.
//   Each sub-category row must have:
//     param_name = 'SUB-CATEGORY - <exact Category title>'
//     title      = sub-category display name
//   Example: for category "Travel", use param_name = 'SUB-CATEGORY - Travel'.
//
// A category with no entry (or an empty array) here AND no matching live rows
// is treated as having NO sub-categories, so the Sub-Category field is
// hidden entirely for that category - see getSubCategoryOptions() below and
// `isSubCategoryVisible`/`filteredSubCategories` in ReimbursementAdd.vue.
export const USE_LIVE_SUB_CATEGORY_LIST = true
export const SUB_CATEGORY_OPTIONS_BY_CATEGORY = {}

/**
 * Prefer `data.sub_category` from GET /api/admin/config/expense/.
 * If that array is missing/empty, scan every array on `data` for rows whose
 * `param_name` starts with `SUB-CATEGORY -` (same rows Expense Tracker uses).
 */
export function extractExpenseSubCategoryRows(expenseConfigData) {
  if (!expenseConfigData || typeof expenseConfigData !== 'object') return []
  const preferred = expenseConfigData.sub_category
  if (Array.isArray(preferred) && preferred.length > 0) return preferred

  return Object.values(expenseConfigData)
    .filter(Array.isArray)
    .flat()
    .filter(
      row =>
        row
        && typeof row.param_name === 'string'
        && row.param_name.startsWith('SUB-CATEGORY - '),
    )
}

/**
 * Sub-category titles for a selected Category title.
 * Live rows match: param_name === `SUB-CATEGORY - ${category}` (exact title).
 * Same filter as Expense Tracker / ExpenseAdd.
 */
export function getSubCategoryOptions(category, liveSubCategoryList) {
  if (!category) return []
  const categoryTitle = String(category).trim()
  const staticList = SUB_CATEGORY_OPTIONS_BY_CATEGORY[categoryTitle]
    || SUB_CATEGORY_OPTIONS_BY_CATEGORY[category]
    || []
  const prefix = `SUB-CATEGORY - ${categoryTitle}`
  const liveList = (Array.isArray(liveSubCategoryList) ? liveSubCategoryList : [])
    .filter(subCat => subCat && String(subCat.param_name || '').trim() === prefix)
    .map(subCat => subCat.title)
    .filter(Boolean)

  if (USE_LIVE_SUB_CATEGORY_LIST) {
    return liveList.length > 0 ? liveList : staticList
  }
  return staticList.length > 0 ? staticList : liveList
}

// --- Location ------------------------------------------------------------
// Primary source is LIVE (`location-service.js` -> `GET locations`) by
// default. Set to `false` and populate LOCATION_OPTIONS to use a fixed
// static list instead.
export const USE_LIVE_LOCATION_LIST = true
export const LOCATION_OPTIONS = []

// ------------------------------------------------------------------------
// Categories (case-insensitive, substring match against Category AND
// Sub-category) for which the "TDS Applicable?" toggle is shown on the
// Create form. This is a finance-domain judgment call, NOT a hardcoded
// backend rule - EDIT THIS LIST FREELY as new finance-sensitive categories
// are introduced. Seeded with:
//   - "Salary"              -> salary payments/advances (TDS u/s 192)
//   - "Professional Fees"   -> consultant/professional payouts (TDS u/s 194J)
//   - "Contractual Payment" -> contractor/vendor payments (TDS u/s 194C)
// These are the most common finance-sensitive categories that typically
// attract a TDS withholding obligation in Indian payroll/vendor accounting;
// add more (e.g. "Rent", "Commission") here as the org's category list grows.
// ------------------------------------------------------------------------
export const CATEGORIES_REQUIRING_TDS_PROMPT = [
  'Salary',
  'Professional Fees',
  'Contractual Payment',
]

export function categoryRequiresTDSPrompt(category, subCategory) {
  const haystacks = [category, subCategory]
    .filter(Boolean)
    .map(v => String(v).toLowerCase())
  if (haystacks.length === 0) return false
  return CATEGORIES_REQUIRING_TDS_PROMPT.some(configured => {
    const needle = configured.toLowerCase()
    return haystacks.some(h => h.includes(needle))
  })
}

// ------------------------------------------------------------------------
// Payroll cycle — default list filter is 25th of previous month → 25th of
// current month (common finance reimbursement window).
// ------------------------------------------------------------------------
export const REIMBURSEMENT_CYCLE_DAY = 25

/** API `dateFilterSelected` value when filtering by expense date. */
export const REIMBURSEMENT_DATE_FILTER_FIELD = 'expense'

/** @returns {string} YYYY-MM-DD */
export function formatReimbursementDateYMD(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Default expense-date filter: 25th of previous calendar month through
 * 25th of current calendar month (inclusive end).
 * @returns {{ startDate: string, endDate: string }}
 */
export function getDefaultReimbursementCycleDates(referenceDate = new Date()) {
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate)
  const year = ref.getFullYear()
  const month = ref.getMonth()

  const endDate = new Date(year, month, REIMBURSEMENT_CYCLE_DAY)
  const startDate = new Date(year, month - 1, REIMBURSEMENT_CYCLE_DAY)

  return {
    startDate: formatReimbursementDateYMD(startDate),
    endDate: formatReimbursementDateYMD(endDate),
  }
}

// ------------------------------------------------------------------------
// Status display — stored values stay "Pending Level 1" etc.; pills/filters
// show HR Pending / Finance Pending / Manager Pending (see stage role names
// in reimbursement-permissions.js). Legacy `is_approved` is still the fallback.
// ------------------------------------------------------------------------
export function deriveReimbursementStatus(row) {
  return formatReimbursementStatusLabel(row)
}

export function deriveReimbursementStatusVariant(row) {
  return deriveDisplayStatusVariant(row)
}

// ------------------------------------------------------------------------
// Email notifications (mirrors Attendance DB `email_templates` + mail-hr)
// ------------------------------------------------------------------------

/** Master switch — set false to suppress all reimbursement notification sends.
 *  Backend owns mail on create/approve/reject (reimbursementEmailService.js). */
export const REIMBURSEMENT_EMAIL_ENABLED = false

/**
 * Optional HR / Finance CC appended to every reimbursement notification
 * (mirrors `email_ids` on DB template rows). Comma-separated.
 */
export const REIMBURSEMENT_EMAIL_CC = 'hr@sattvahuman.com'

/**
 * Approver queue routing is permission-based only (CASL subjects
 * `reimbursement_level1` / `level2` / `final`). Do NOT hardcode fallback
 * inboxes (hr@ / finance@ / ritesh@) on the client — backend must resolve
 * real approver emails via getApproverEmailsByPermission (or equivalent)
 * and must not invent queue addresses when no permission-matched users exist.
 */

export default {
  REQUIRED_FIELDS,
  FIELD_LABELS,
  isFieldRequired,
  resolveOptions,
  USE_LIVE_PAYMENT_MODE_LIST,
  PAYMENT_MODE_OPTIONS,
  USE_LIVE_CLIENT_LIST,
  ACCOUNT_NAME_OPTIONS,
  BUSINESS_UNIT_DEFAULT,
  SEND_BUSINESS_UNIT,
  VERTICAL_OPTIONS,
  USE_LIVE_GST_NUMBER_LIST,
  GST_NUMBER_OPTIONS,
  SEND_GST_AMOUNT,
  USE_LIVE_CATEGORY_LIST,
  CATEGORY_OPTIONS,
  REIMBURSEMENT_FIXED_CATEGORY,
  USE_LIVE_SUB_CATEGORY_LIST,
  SUB_CATEGORY_OPTIONS_BY_CATEGORY,
  extractExpenseSubCategoryRows,
  getSubCategoryOptions,
  USE_LIVE_LOCATION_LIST,
  LOCATION_OPTIONS,
  CATEGORIES_REQUIRING_TDS_PROMPT,
  categoryRequiresTDSPrompt,
  deriveReimbursementStatus,
  deriveReimbursementStatusVariant,
  REIMBURSEMENT_CYCLE_DAY,
  REIMBURSEMENT_DATE_FILTER_FIELD,
  formatReimbursementDateYMD,
  getDefaultReimbursementCycleDates,
  REIMBURSEMENT_FILE_UPLOAD_ENABLED,
  REIMBURSEMENT_FILE_FIELD,
  REIMBURSEMENT_SUPPORTING_DOC_FIELD,
  REIMBURSEMENT_MAX_FILE_SIZE_BYTES,
  REIMBURSEMENT_MAX_FILE_SIZE_LABEL,
  getReimbursementFileSizeError,
  getReimbursementAttachmentRequiredError,
  getReimbursementFilePath,
  getReimbursementFileName,
  REIMBURSEMENT_EMAIL_ENABLED,
  REIMBURSEMENT_EMAIL_CC,
}
